import { useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ASSETS } from "@/lib/assets";

/**
 * Cinematic infrastructure video section.
 *
 * Sits on the homepage between "Three Layers of Trust" and "Identity → Hardware →
 * Policy → Ownership". The section background stays light and architectural; the
 * video itself lives inside a refined dark cinematic frame so its emotional
 * contrast amplifies rather than fights the surrounding white/silver palette.
 */
export const InfrastructureVideo = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState(false);

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      try {
        await v.play();
        setPlaying(true);
        setStarted(true);
      } catch (e) {
        setPlaying(false);
        setError(true);
      }
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const enterFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    const req =
      v.requestFullscreen ||
      v.webkitRequestFullscreen ||
      v.webkitEnterFullscreen;
    if (req) req.call(v);
  };

  return (
    <section
      className="py-24 md:py-32 bg-white"
      data-testid="infrastructure-video-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="max-w-7xl">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Cinematic Infrastructure
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight text-[#06102A]">
              See Trust Infrastructure in Motion
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#06102A]/70">
              Vaultavo combines biometric identity, hardware-secured custody, and
              policy-enforced governance into a unified infrastructure platform
              built for regulated digital finance.
            </p>
          </Reveal>
        </div>

        <Reveal delay={200}>
          <div className="mt-14 md:mt-16">
            {/* Outer architectural frame */}
            <div
              className="relative rounded-3xl p-2 md:p-3"
              style={{
                background:
                  "linear-gradient(135deg, #FAFBFC 0%, #E9EBEF 50%, #FAFBFC 100%)",
                boxShadow:
                  "0 60px 120px -40px rgba(6,16,42,0.32), inset 0 1px 0 rgba(255,255,255,0.7)",
                border: "1px solid rgba(6,16,42,0.08)",
              }}
            >
              {/* Cinematic dark video container */}
              <div className="relative rounded-2xl overflow-hidden bg-[#06102A] aspect-video">
                <video
                  ref={videoRef}
                  data-testid="infrastructure-video"
                  poster={ASSETS.heroFinal}
                  preload="metadata"
                  muted={muted}
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                  onPlay={() => {
                    setPlaying(true);
                    setStarted(true);
                  }}
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                  onError={() => setError(true)}
                  onClick={togglePlay}
                >
                  <source src={ASSETS.video} type="video/mp4" />
                  <source src={ASSETS.video} type="video/quicktime" />
                </video>

                {/* Fallback message if the browser cannot play this codec */}
                {error && (
                  <div
                    data-testid="infrastructure-video-error"
                    className="absolute inset-0 grid place-items-center bg-[#06102A] text-white text-center px-6"
                  >
                    <div>
                      <p className="font-display text-lg md:text-xl">
                        This browser cannot play the embedded film.
                      </p>
                      <a
                        href={ASSETS.video}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-[#06102A] text-sm font-medium px-5 py-2.5"
                      >
                        Open the film in a new tab
                      </a>
                    </div>
                  </div>
                )}

                {/* Soft top gradient (only before first play) */}
                {!started && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(6,16,42,0.10) 0%, transparent 35%, transparent 65%, rgba(6,16,42,0.35) 100%)",
                    }}
                  />
                )}

                {/* Centered play button — visible when paused */}
                {!playing && (
                  <button
                    onClick={togglePlay}
                    data-testid="infrastructure-video-play"
                    aria-label="Play Vaultavo film"
                    className="group absolute inset-0 grid place-items-center cursor-pointer"
                  >
                    <span
                      className="relative grid place-items-center h-20 w-20 md:h-24 md:w-24 rounded-full transition-transform duration-500 group-hover:scale-105"
                      style={{
                        background:
                          "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.96) 0%, rgba(229,231,235,0.92) 60%, rgba(184,189,200,0.9) 100%)",
                        boxShadow:
                          "0 30px 60px -20px rgba(6,16,42,0.55), 0 0 0 8px rgba(255,255,255,0.06), inset 0 -8px 22px rgba(6,16,42,0.18), inset 0 6px 14px rgba(255,255,255,0.9)",
                      }}
                    >
                      <Play
                        className="h-7 w-7 md:h-9 md:w-9 text-[#06102A] ml-1"
                        strokeWidth={1.5}
                        fill="#06102A"
                      />
                      {/* Pulse ring */}
                      <span
                        className="absolute inset-0 rounded-full border border-white/40 vt-pulse pointer-events-none"
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                )}

                {/* Control bar — visible once started */}
                {started && (
                  <div
                    data-testid="infrastructure-video-controls"
                    className="absolute bottom-3 left-3 right-3 flex items-center gap-2 z-10"
                  >
                    <button
                      onClick={togglePlay}
                      aria-label={playing ? "Pause" : "Play"}
                      data-testid="infrastructure-video-toggle"
                      className="h-9 w-9 grid place-items-center rounded-full bg-white/15 backdrop-blur-md text-white border border-white/20 hover:bg-white/25 transition-colors"
                    >
                      {playing ? (
                        <Pause className="h-4 w-4" strokeWidth={1.8} />
                      ) : (
                        <Play className="h-4 w-4" strokeWidth={1.8} fill="currentColor" />
                      )}
                    </button>
                    <button
                      onClick={toggleMute}
                      aria-label={muted ? "Unmute" : "Mute"}
                      data-testid="infrastructure-video-mute"
                      className="h-9 w-9 grid place-items-center rounded-full bg-white/15 backdrop-blur-md text-white border border-white/20 hover:bg-white/25 transition-colors"
                    >
                      {muted ? (
                        <VolumeX className="h-4 w-4" strokeWidth={1.8} />
                      ) : (
                        <Volume2 className="h-4 w-4" strokeWidth={1.8} />
                      )}
                    </button>
                    <button
                      onClick={enterFullscreen}
                      aria-label="Fullscreen"
                      data-testid="infrastructure-video-fullscreen"
                      className="ml-auto h-9 w-9 grid place-items-center rounded-full bg-white/15 backdrop-blur-md text-white border border-white/20 hover:bg-white/25 transition-colors"
                    >
                      <Maximize2 className="h-4 w-4" strokeWidth={1.8} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Footer caption */}
            <div className="mt-6 flex items-center justify-between gap-4 max-w-7xl">
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/55 font-mono-vt">
                Vaultavo · Infrastructure Film
              </p>
              <p className="text-xs text-[#06102A]/45 font-mono-vt">
                2 min · Click play to begin
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default InfrastructureVideo;
